import { Conditions } from '@/api/enums';
import { equal, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { PageTemplate, PageTemplatesEnum } from '@/client/page-templates';
import { ThumbnailState } from '@/client/thumbnails/enums';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TemplatesFilters } from '@/components/templates';
import ThumbnailsGenerationNotify from '@/components/thumbnails/ThumbnailsGenerationNotify';
import {
  useDeletePageTemplate,
  usePageTemplates,
  useUpdatePageTemplate,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTemplatesFilters } from '@/hooks/templates';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { LandingPageTemplatesTabs } from '@/pages/landing-page-templates';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { ImageCard } from '@/ui/image-card';
import { CardGridContainer, FlexContainer } from '@/ui/styled-ui';
import { displayCreatedBy } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const LandingPageTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const account = useAppSelector(selectCurrentAccount);
  const deleteLandingPageTemplate = useDeletePageTemplate();
  const updateTemplate = useUpdatePageTemplate();
  const { can } = usePermission();
  const {
    take,
    search,
    nameFilter,
    skip,
    handleSearch,
    handleChangeSkip,
    handleChangeDefaultRows,
  } = useTemplatesFilters();

  const breadCrumbItems = [
    {
      label: (account as Account)?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('templates.predefined.page'),
      url: RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_PREDEFINED](),
      template: AppBreadCrumbTemplate,
    },
  ];

  const activeFilters = account?.isSystem ? [] : [equal('active', true)];

  // Templates filtered by account=null, will return only predefined templates
  const { isLoading, pageTemplates, refetch } = usePageTemplates({
    take,
    skip,
    filters: [
      { field: 'account', condition: Conditions.EQUAL, value: null },
      ...nameFilter,
      equal('type', PageTemplatesEnum.LANDING),
      ...activeFilters,
    ],
  });

  const handleToggleTemplateStatus = async (
    pageTemplateId: string,
    active: boolean,
  ) => {
    try {
      await updateTemplate.update({
        pageTemplateId,
        updates: { active: !active },
      });
      await refetch();
      toast?.success(
        t('toast.success'),
        active
          ? t('templates.deactivated.success')
          : t('templates.activated.success'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleDeleteLandingPageTemplate = async (
    landingPageTemplateId: string,
  ) => {
    try {
      await deleteLandingPageTemplate.delete(landingPageTemplateId);
      setTimeout(() => {
        refetch();
      }, 100);
      toast?.success(t('toast.success'), t('templates.predefined.page.delete'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const menuItems = (landingPageTemplate: PageTemplate, account: Account) => {
    const actions: MenuItem[] = [];
    if (can(Actions.CREATE, Subjects.PAGES)) {
      actions.push({
        label: t('generic.copyEdit'),
        icon: 'pi pi-copy',
        command: () =>
          navigate(
            RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_CREATE](),
            {
              state: { landingPageTemplate },
            },
          ),
      });
    }

    if (can(Actions.UPDATE, Subjects.PAGES) && account?.isSystem) {
      actions.push(
        ...[
          {
            label: t('generic.edit'),
            icon: 'pi pi-pencil',
            command: () =>
              navigate(
                RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_UPDATE](
                  landingPageTemplate.id,
                ),
              ),
          },
          {
            label: landingPageTemplate.active
              ? t('generic.deactivate')
              : t('generic.activate'),
            icon: landingPageTemplate.active
              ? 'pi pi-minus-circle'
              : 'pi pi-check',
            command: () =>
              setDialogData({
                type: DialogType.CONFIRMATION,
                show: true,
                header: landingPageTemplate.active
                  ? t('dialog.deactivate.confirm')
                  : t('dialog.activate.confirm'),
                message: landingPageTemplate.active
                  ? t('templates.page.deactivate')
                  : t('templates.page.activate'),
                onAccept: async () =>
                  await handleToggleTemplateStatus(
                    landingPageTemplate.id,
                    landingPageTemplate.active,
                  ),
              }),
          },
        ],
      );
    }
    if (can(Actions.DELETE, Subjects.PAGES) && account?.isSystem) {
      actions.push({
        label: t('generic.delete'),
        icon: 'pi pi-times',
        command: () =>
          setDialogData({
            type: DialogType.CONFIRMATION,
            show: true,
            header: t('dialog.delete.confirm'),
            message: t('dialog.delete.predefined.page'),
            onAccept: async () =>
              await handleDeleteLandingPageTemplate(landingPageTemplate.id),
          }),
      });
    }

    return actions;
  };

  return (
    <>
      <AppBreadCrumb model={breadCrumbItems} />
      <FlexContainer justify="space-between">
        <h1>{t('templates.predefined.page')}</h1>
        {can(Actions.CREATE, Subjects.EMAIL_TEMPLATES) && account?.isSystem && (
          <AppButton
            label={t('button.createNew')}
            severity="secondary"
            onClick={() => {
              navigate(
                RedirectPaths[
                  RedirectPathsEnum.LANDING_PAGE_TEMPLATES_CREATE
                ](),
              );
            }}
          />
        )}
      </FlexContainer>
      <LandingPageTemplatesTabs />

      <TemplatesFilters
        count={pageTemplates?.count ?? 0}
        take={take}
        skip={skip}
        onPageChange={handleChangeSkip}
        onRowsChange={handleChangeDefaultRows}
        onSearch={handleSearch}
        search={search}
      />
      {!isLoading && !pageTemplates?.result.length && (
        <FlexContainer direction="column" className="mt-5">
          <h3>{t('templates.page.empty')}</h3>
        </FlexContainer>
      )}
      {isLoading && !pageTemplates && (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
          <h3>{t('templates.page.loading')}</h3>
        </FlexContainer>
      )}
      {!isLoading && !!pageTemplates?.result.length && account && (
        <>
          <ThumbnailsGenerationNotify
            refetch={refetch}
            templates={pageTemplates?.result}
          />
          <CardGridContainer>
            {pageTemplates.result.map((template) => (
              <ImageCard
                key={template.id}
                id={template.id}
                title={template.name}
                thumbnail={template.thumbnail?.signedUrl}
                isLoading={
                  template.thumbnailState === ThumbnailState.PROCESSING
                }
                author={displayCreatedBy(t, template.createdBy)}
                templateType="page"
                interactionType="withActions"
                showStatus={account.isSystem}
                activeStatus={template.active}
                actions={menuItems(template, account)}
              />
            ))}
          </CardGridContainer>
        </>
      )}
    </>
  );
};
